// The Cairo/Montserrat/Mulish/AvantGarde/Roboto imports are gone: importing a
// font asset is what makes webpack emit it, so keeping them would have shipped
// ~5MB of ttf that nothing paints with any more. Those family names are still
// honoured — see ALIASED_FAMILIES below — they just resolve to the Noto files.
// The .ttf files themselves are left in src/Assets/Fonts, unreferenced.
import { rgba, normalize } from 'polished'
import { createGlobalStyle } from 'styled-components'
import { FawryStyles } from './FawryStyles'
import { OpayStyles } from './OpayStyles'
import {
	MaterialIconsOutlined,
	MaterialIconsRegular,
	MaterialIconsRound,
	MaterialIconsSharp,
	MaterialIconsTwoTone,
} from '@Assets/Fonts/MaterialIcons'
import { FAQsTable } from '@eduact/ed-system'
import { generateMaterialIconsFontFaces, MaterialIconFontFace } from '@eduact/ed-system'
import { NotoSansLatin, NotoSansLatinExt } from '@Assets/Fonts/NotoSans'
import { NotoSansArabicArabic } from '@Assets/Fonts/NotoSansArabic'

/**
 * The product's typeface: Noto Sans for Latin, Noto Sans Arabic for Arabic.
 *
 * Declared here rather than through `createGlobalFont`, which emits one
 * @font-face per weight and cannot express either a variable weight range or a
 * unicode-range. Both files carry the whole 100–900 axis, so every weight the
 * UI asks for comes from a single download instead of one file per weight.
 *
 * The two families are combined as a stack rather than switched on `dir`. The
 * browser then resolves per glyph run: Latin from Noto Sans, Arabic falling
 * through to Noto Sans Arabic because Noto Sans has no Arabic coverage. That
 * is what a `dir`-based switch cannot do — an Arabic page here is full of
 * Latin instructor names and figures, and a mixed line gets each script from
 * the face designed for it.
 *
 * unicode-range means a page downloads only the subsets it actually paints: an
 * English page never fetches the Arabic file, and vice versa.
 */
export const FONT_STACK = `'Noto Sans', 'Noto Sans Arabic', sans-serif`

const LATIN_RANGE = `U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329,
			U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD`
const LATIN_EXT_RANGE = `U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329,
			U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF`
const ARABIC_RANGE = `U+0600-06FF, U+0750-077F, U+0870-088E, U+0890-0891, U+0898-08E1, U+08E3-08FF, U+200C-200E,
			U+2010-2011, U+204F, U+2E41, U+FB50-FDFF, U+FE70-FEFF, U+102E0-102FB, U+10E60-10E7E, U+10EFD-10EFF,
			U+1EE00-1EEFF`

const face = (family: string, src: string, range: string) => `
	@font-face {
		font-family: '${family}';
		src: url(${src}) format('woff2');
		font-weight: 100 900;
		font-style: normal;
		font-display: swap;
		unicode-range: ${range};
	}`

/**
 * Families that only exist inside third-party CSS we cannot edit — the vendored
 * @eduact/ed-components and student-theme hardcode `font-family: 'Roboto'`,
 * `'Montserrat'` and `'AvantGarde'` on inputs, dropdowns, steppers and exam
 * text. Pointing those names at the Noto files is the only way to reach them
 * without patching node_modules, and it is what the product wants anyway: one
 * typeface everywhere. Montserrat in particular carries no Arabic glyphs, so
 * Arabic inside those components was falling back to a system font.
 *
 * Each alias gets all three subsets, because a single family name has to cover
 * both scripts here.
 */
const ALIASED_FAMILIES = ['Roboto', 'Montserrat', 'Mulish', 'AvantGarde', 'Cairo']

const notoFontFaces = `
	${face('Noto Sans', NotoSansLatin, LATIN_RANGE)}
	${face('Noto Sans', NotoSansLatinExt, LATIN_EXT_RANGE)}
	${ALIASED_FAMILIES.map(
		f => `${face(f, NotoSansLatin, LATIN_RANGE)}${face(f, NotoSansLatinExt, LATIN_EXT_RANGE)}${face(f, NotoSansArabicArabic, ARABIC_RANGE)}`
	).join('')}
	${face('Noto Sans Arabic', NotoSansArabicArabic, ARABIC_RANGE)}
`

const materialFonts: MaterialIconFontFace = {
	Outlined: { src: MaterialIconsOutlined },
	Regular: { src: MaterialIconsRegular, mapped_name: '' },
	Round: { src: MaterialIconsRound },
	Sharp: { src: MaterialIconsSharp },
	TwoTone: { src: MaterialIconsTwoTone, mapped_name: 'TwoTone' },
}

const GlobalStyles = createGlobalStyle`
${normalize()}

* {
  /* outline: ${process.env.NODE_ENV === 'development' ? ' 1px solid tomato' : ''}; */
}


iframe {
	/* pointer-events: ${process.env.NODE_ENV === 'development' ? '' : 'none'}; */
}
	${generateMaterialIconsFontFaces(materialFonts)}
	/* The legacy families are no longer registered against their own files —
	   notoFontFaces re-declares those names against the Noto ones. Leaving the
	   old createGlobalFont() calls here would ship ~5MB of ttf and, being
	   declared later, would win. Material Icons keep their own faces. */
	${notoFontFaces}

  * {
    box-sizing: border-box;
	
	}

  html ,body , #root{ 
	  padding : 0;
	  margin : 0;
	
	img {
		max-width: 100%;
	}
}
html ,body ,#root{
	  font-family: ${FONT_STACK};
	  ::-webkit-scrollbar {
		appearance: none;
		width : 5px;
	  }
	  ::-webkit-scrollbar-track{
		border-radius : 10px;
		background: ${props => props.theme.colors.silver}
	}
	::-webkit-scrollbar-thumb{
		border-radius : 10px;
		  background: ${props => props.theme.colors.spanishGray}
	  }
  }
  * {
  ::-webkit-scrollbar {
		appearance: none;
		width : 5px;
	  }
	  ::-webkit-scrollbar-track{
		border-radius : 10px;
		background: ${props => props.theme.colors.silver}
	}
	::-webkit-scrollbar-thumb{
		border-radius : 10px;
		  background: ${props => props.theme.colors.spanishGray}
	  }
  }
  html {
	  overflow-x :hidden;
	  height : 100%;
  }
  body , #root{
	min-height :100%;
  }
  /*
   * The rtl override that used to live here set Cairo on html/body only — but
   * #root sits between body and the app and set its own font-family, so the
   * whole tree inherited that instead and Cairo was never painted. Nothing
   * dir-specific is needed now: the stack on #root resolves Arabic and Latin
   * per glyph run, in both directions.
   */
  html {
  box-sizing: border-box;
}
*, *:before, *:after {
  box-sizing: inherit;
}
 
  
  ${FawryStyles}
  ${OpayStyles}
  ${FAQsTable}

  :root {
	/* Single source of truth for the typeface. Components reference
	   var(--font-sans) rather than naming a family, so the stack is changed in
	   one place. */
	--font-sans: ${FONT_STACK};
	${({ theme }) => `${theme.mediaQueries.min(1366)}{
		font-size :11px;
	}`}
  ${({ theme }) => `${theme.mediaQueries.min(1440)}{
		font-size :12px;
	}`}
	 ${({ theme }) => `${theme.mediaQueries.xlarge}{
		font-size :12px;
	}`}
	${({ theme }) => `${theme.mediaQueries.min(1536)}{
		font-size :16px;
	}`}
}
:root {
	--app-header-height: 4.938rem;
}

.command-palette{
	z-index: 1000;
}

/* WaselChat launcher, hidden without unmounting (the vendor ships no hide
   API, but the iframe lives in our DOM). Toggled by the WaselChat component
   on the exam player routes. */
html.waselchat-hidden #abwaabchat-frame {
	display: none !important;
}
  `
export default GlobalStyles
